import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_DRAFT = { name: "", email: "", phone: "" };

export const isExternalRecipientComplete = (external) =>
  Boolean(external?.name?.trim() && external?.email?.trim());

export const hasRecipient = (client, external) =>
  Boolean(client?.id) || isExternalRecipientComplete(external);

/**
 * Who the reminder is for: an existing client, or a person outside the client list captured by name
 * and email. The two are mutually exclusive, so picking one clears the other.
 */
const ReminderRecipientField = ({
  client,
  external,
  onClientChange,
  onExternalChange,
  options = [],
  loading = false,
  inputValue,
  onInputChange,
  listboxProps,
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [draftError, setDraftError] = useState("");

  const hasExternal = isExternalRecipientComplete(external);

  useEffect(() => {
    if (hasExternal) setAdding(false);
  }, [hasExternal]);

  const setDraftField = (field) => (e) => {
    setDraft((prev) => ({ ...prev, [field]: e.target.value }));
    setDraftError("");
  };

  const cancelExternal = () => {
    setAdding(false);
    setDraft({ ...EMPTY_DRAFT });
    setDraftError("");
  };

  const saveExternal = () => {
    const name = draft.name.trim();
    const email = draft.email.trim();
    if (!name || !email) {
      setDraftError("Name and email are required.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setDraftError("Enter a valid email.");
      return;
    }
    onClientChange(null);
    onExternalChange({ name, email, phone: draft.phone.trim() });
    cancelExternal();
  };

  const startEditing = () => {
    setDraft({
      name: external?.name || "",
      email: external?.email || "",
      phone: external?.phone || "",
    });
    setDraftError("");
    setAdding(true);
  };

  const clearExternal = () => {
    onExternalChange(null);
    cancelExternal();
  };

  if (hasExternal && !adding) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Reminder for
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={`${external.name} (not in system)`}
            color="secondary"
            variant="outlined"
            size="small"
            onDelete={clearExternal}
          />
          <Typography variant="caption" color="text.secondary">
            {external.email}
            {external.phone ? ` · ${external.phone}` : ""}
          </Typography>
          <Link component="button" type="button" variant="caption" onClick={startEditing}>
            Edit
          </Link>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Autocomplete
        options={options}
        filterOptions={(x) => x}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, value, reason) => {
          if (reason === "reset") return;
          onInputChange(value);
        }}
        value={client || null}
        onChange={(_, v) => {
          onClientChange(v);
          if (v) onExternalChange(null);
        }}
        getOptionLabel={(o) => o?.name || ""}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Client *"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={listboxProps}
      />

      {!adding && (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAdding(true)}
          sx={{ mt: 1, display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 500 }}
        >
          <PersonAddOutlinedIcon sx={{ fontSize: 16 }} />
          Client not in the system? Add their details
        </Link>
      )}

      <Collapse in={adding}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 1,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "grey.50"),
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Add a client not in the system
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Used for this reminder only. They are not added to your client list.
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label="Full name *"
              size="small"
              fullWidth
              value={draft.name}
              onChange={setDraftField("name")}
              autoFocus
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Email *"
                type="email"
                size="small"
                fullWidth
                value={draft.email}
                onChange={setDraftField("email")}
              />
              <TextField
                label="Phone"
                size="small"
                fullWidth
                value={draft.phone}
                onChange={setDraftField("phone")}
              />
            </Stack>
            {draftError && (
              <Typography variant="caption" color="error">
                {draftError}
              </Typography>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={cancelExternal}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={saveExternal}>
                Add client
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default ReminderRecipientField;
